import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { useSession } from "../auth/session";
import { beginLogin, isSsoConfigured } from "../auth/oidc";
import { Role } from "../auth/capabilities";
import { resolveTenantSlug, tenantPath } from "../config";
import { fetchIdpHint, useTenantBranding } from "../auth/tenantBranding";

// Landing / login view (E1 US-1.1). Verso-branded: the tenant identity sits in
// the AuthLayout branding slot; this view carries the "sign in with your
// organisation" action (internal users → SSO, coral CTA) and the external-learner
// email-link entry point below a divider. Credential entry + 2FA happen on the
// Keycloak-themed pages, not here.
//
// Everything this view decides comes from the same unauthenticated tenant lookup the
// layout reads (`GET /api/auth/tenant`, shared — one request for the page):
//   • unknown tenant → the story's "Organisaatiota ei löytynyt" state, with NO
//     sign-in button, instead of a sign-in form for an organisation that does not exist;
//   • the external-learner link appears only where the tenant allows external learners.
//     `EmailLinkService` refuses to issue a link for a tenant that has not opted in, so
//     the link used to be a dead end that confirmed nothing except that it was dead.
//   • WHERE the sign-in button sends people. A federated tenant's lookup answers its
//     broker's alias, which rides to Keycloak as `kc_idp_hint` and lands them in their
//     employer's own directory (ADR-0014 §12 C-6). It changes nothing that is drawn:
//     one button, and the hint is the only difference between the two kinds of tenant.
export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { setSession } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tenant = resolveTenantSlug();
  const branding = useTenantBranding();
  const expired = params.get("reason") === "session_expired";
  const returnTo = params.get("returnTo") ?? "/";

  function signIn() {
    const locale = i18n.language.split("-")[0];
    if (isSsoConfigured()) {
      // Internal users authenticate at the IdP via authorization-code + PKCE (US-1.1).
      // The locale rides along in ui_locales so the choice survives the redirect; the
      // callback (/auth/callback) exchanges the code and establishes the session.
      //
      // `kc_idp_hint` is what makes a FEDERATED customer's people reach their own identity provider from
      // this button rather than from a URL somebody hands them (ADR-0014 §12 C-6). It is resolved from the
      // tenant lookup this page already made — awaited, not read off `branding`, so clicking before the
      // lookup settles still sends the hint (see fetchIdpHint). Null for every tenant without a federation
      // in force, which signs in exactly as it did before.
      void fetchIdpHint(tenant).then((idpHint) =>
        beginLogin({ tenant, returnTo, locale, idpHint }),
      );
      return;
    }
    // Dev fallback (no IdP configured locally): establish a placeholder session
    // so the guarded routes stay exercisable without a running Keycloak. The demo
    // user is granted admin + platform roles so every gated surface is reachable.
    setSession({
      userId: "demo",
      displayName: "Demo",
      tenantSlug: tenant,
      roles: [Role.Admin, Role.PlatformAdmin],
    });
    void navigate(returnTo, { replace: true });
  }

  // Unknown tenant: a terminal state, not a banner above a usable form. There is nothing to sign in
  // to, so the sign-in button is gone (story US-1.1: "ei kirjautumispainiketta").
  if (branding.status === "unknown") {
    return (
      <AuthLayout>
        <div className="auth__head">
          <h1 className="auth__title" data-testid="login-unknown-tenant">
            {t("states.unknownTenant")}
          </h1>
          <p className="auth__text" role="alert">
            {t("auth.unknownTenantBody")}
          </p>
        </div>
      </AuthLayout>
    );
  }

  // The external-learner entry point follows the TENANT, not the URL. While the lookup is in flight it
  // is withheld rather than guessed, so it never appears only to vanish a moment later. With no tenant
  // addressable in the URL (bare apex host) there is nothing to withhold it on, and the request page
  // itself answers for the tenant it resolves.
  const showExternalLearner =
    branding.status === "none" ||
    (branding.status === "ready" && branding.branding.allowsExternalLearners);

  // Keep the tenant in the URL across the hop: on `/t/<slug>/login` a bare `/login/email` would drop
  // the only thing that tells the backend which organisation the learner is signing in to. The rule is
  // `tenantPath`'s, shared with the two pages this link leads to (src/config.ts) — they hop back here and
  // between each other, and three copies of one prefix rule is how the tenant gets lost on one of them.
  const emailLinkPath = tenantPath("/login/email");

  return (
    <AuthLayout>
      <div className="auth__head">
        <h1 className="auth__title">{t("auth.loginTitle")}</h1>
        <p className="auth__subtitle">{t("auth.loginSubtitle")}</p>
      </div>

      {expired && (
        <p role="alert" className="banner banner--warn">
          {t("auth.sessionExpired")}
        </p>
      )}

      {branding.status === "error" && (
        <p role="alert" className="banner banner--error">
          {t("auth.brandingUnavailable")}
        </p>
      )}

      <div className="auth__actions">
        <button type="button" className="btn btn--primary btn--block" onClick={signIn}>
          {t("auth.loginWithOrg")}
        </button>

        {showExternalLearner && (
          <>
            <div className="auth__divider">{t("auth.or")}</div>

            <Link
              className="btn btn--block"
              to={emailLinkPath}
              data-testid="login-external-learner"
            >
              {t("auth.externalLearner")}
            </Link>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
